import React, { ReactNode, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Settings, Menu, Wifi, Activity, HelpCircle, ChevronDown, ChevronRight, Boxes, Building2, Users as UsersIcon, Shield, LayoutDashboard, LineChart, Table, LogOut, User, Download } from 'lucide-react';
import { Button, Menu as MantineMenu, ActionIcon, Divider, Avatar, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import '@mantine/core/styles.css';
import '../../styles/layout/AppLayout.scss';
import LanguageSwitcher from '../common/LanguageSwitcher';
import ThemeSwitcher from '../common/ThemeSwitcher';
import { useAuth } from '../../contexts/AuthContext';

interface AppLayoutProps {
  children: ReactNode;
  className?: string;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children, className: _className = '' }) => {
  const [isMobile, setIsMobile] = useState<boolean>(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const [isWide, setIsWide] = useState<boolean>(() => (typeof window !== 'undefined' ? window.innerWidth >= 1024 : false));
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [_headerBannerLoaded, setHeaderBannerLoaded] = useState<boolean>(false);
  const [sidebarBannerLoaded, setSidebarBannerLoaded] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isAdmin, logout } = useAuth();
  const connectGroupActive = ['/connect', '/gateway', '/sensor'].some(p => location.pathname.startsWith(p));
  const [connectOpen, setConnectOpen] = useState<boolean>(connectGroupActive);

  // Ensure favicon uses the TimberTrack logo from public/
  useEffect(() => {
    try {
      const href = `${process.env.PUBLIC_URL || ''}/logo-dark-sm.png`;
      const setIcon = (rel: string) => {
        let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
        if (!link) {
          link = document.createElement('link');
          link.rel = rel;
          document.head.appendChild(link);
        }
        link.type = 'image/png';
        link.href = href;
      };
      setIcon('icon');
      setIcon('apple-touch-icon');
    } catch (e) {
      // no-op: failing to set favicon should not break the app
      // Ignore favicon errors
    }
  }, []);
  const adminGroupActive = location.pathname.startsWith('/admin');
  const [adminOpen, setAdminOpen] = useState<boolean>(adminGroupActive);

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      const mobile = w < 768;
      setIsMobile(mobile);
      setIsWide(w >= 1024);
      // auto-close sidebar on mobile when resizing down
      if (mobile) setSidebarOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Prevent background scroll when mobile sidebar is open
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isMobile && sidebarOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev || '';
      };
    }
  }, [isMobile, sidebarOpen]);

  const navigation = [
    { name: t('navigation.connect'), href: '/connect', icon: Home },
    // nested items rendered under Connect
    { name: t('navigation.summary') || 'Summary', href: '/summary', icon: LayoutDashboard },
    { name: t('navigation.data'), href: '/data', icon: LineChart },
    { name: t('navigation.measurements') || 'Messwerte', href: '/measurements', icon: Table },
    { name: 'Assets', href: '/assets', icon: Boxes },
    // Admin sub-pages are rendered under Admin group (not duplicated here)
    { name: t('navigation.settings'), href: '/settings', icon: Settings },
    { name: 'Downloads', href: '/downloads', icon: Download },
  ];

  const adminItems = [
    { name: t('navigation.adminAssets') || 'Admin Assets', href: '/admin/assets', icon: Boxes },
    { name: t('navigation.customers') || 'Customers', href: '/admin/customers', icon: Building2 },
    { name: t('navigation.users') || 'Users', href: '/admin/users', icon: UsersIcon },
  ];

  const currentTitle = (() => {
    const direct = navigation.find(item => item.href === location.pathname)?.name;
    if (direct) return direct;
    if (location.pathname === '/gateway') return t('navigation.gateway');
    if (location.pathname === '/sensor') return t('navigation.sensor');
    const adminMatch = adminItems.find(item => item.href === location.pathname)?.name;
    if (adminMatch) return adminMatch;
    return t('navigation.summary');
  })();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: 'var(--tt-light-bg)',
      fontFamily: 'Open Sans, sans-serif'
    }}>
      {/* Sidebar (overlay, does not shift content) */}
      <aside style={{
        width: isMobile ? 'min(90vw, 320px)' : '260px',
        background: 'var(--tt-white)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        zIndex: 1000,
        boxShadow: sidebarOpen ? '4px 0 12px rgba(0,0,0,0.12)' : 'none',
        borderRight: isMobile ? 'none' : '1px solid rgba(0,0,0,0.06)',
        transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)'
      }}>
        {sidebarOpen && (
          <div style={{ padding: '20px', width: isMobile ? '100%' : '260px' }}>
            {/* Logo */}
            <Link to="/summary" onClick={() => setSidebarOpen(false)} style={{ textDecoration: 'none' }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                marginBottom: '30px'
              }}>
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/logo-dark-sm.png`} 
                  alt="TimberTrack Logo" 
                  className="tt-invert-on-dark"
                  style={{
                    height: '40px',
                    width: 'auto',
                    // show natural logo colors
                    marginBottom: '12px'
                  }}
                  onError={(e) => {
                    const base = process.env.PUBLIC_URL || '';
                    (e.currentTarget as HTMLImageElement).src = `${base}/logo192.png`;
                  }}
                />
                {/* Optional sidebar banner */}
                {isWide && (
                  <img
                    src={`${process.env.PUBLIC_URL || ''}/image.png`}
                    alt="TimberTrack banner"
                    className="tt-invert-on-dark"
                    style={{
                      display: 'block',
                      maxWidth: '100%',
                      height: 'auto',
                      marginTop: '12px',
                      borderRadius: '8px'
                    }}
                    onLoad={() => setSidebarBannerLoaded(true)}
                    onError={(e) => {
                      // hide if not present in public/
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
                {!(isWide && sidebarBannerLoaded) && (
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--tt-dark)' }}>
                      TimberTrack
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--tt-text)' }}>
                      IoT Control
                    </div>
                  </div>
                )}
              </div>
            </Link>

            {/* Navigation */}
            <nav>
              {/* Connect parent */}
              {(() => {
                const item = { name: t('navigation.connect'), href: '/connect', icon: Home };
                const isActive = connectGroupActive; // highlight group when any child is active
                return (
                  <div key="connect">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Link
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 16px',
                          margin: '4px 0',
                          borderRadius: '8px',
                          color: isActive ? 'var(--tt-primary)' : 'var(--tt-dark)',
                          backgroundColor: isActive ? 'rgba(3,169,244,0.10)' : 'transparent',
                          textDecoration: 'none',
                          fontWeight: isActive ? '600' : '500',
                          transition: 'all 0.3s ease',
                          borderLeft: isActive ? '3px solid var(--tt-primary)' : '3px solid transparent',
                          flex: 1
                        }}
                      >
                        <item.icon size={20} />
                        <span>{item.name}</span>
                      </Link>
                      <button
                        aria-label="toggle-connect"
                        onClick={() => setConnectOpen(v => !v)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: '8px',
                          cursor: 'pointer',
                          color: 'var(--tt-dark)'
                        }}
                      >
                        {connectOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      </button>
                    </div>
                    {/* Sub-links: Gateway, Sensor */}
                    {connectOpen && (
                      <div style={{ marginLeft: '28px' }}>
                        {[
                          { name: t('navigation.gateway'), href: '/gateway', icon: Wifi },
                          { name: t('navigation.sensor'), href: '/sensor', icon: Activity }
                        ].map((sub) => {
                          const subActive = location.pathname === sub.href;
                          return (
                            <Link
                              key={sub.href}
                              to={sub.href}
                              onClick={() => setSidebarOpen(false)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '8px 12px',
                                margin: '2px 0',
                                borderRadius: '16px',
                                color: subActive ? 'var(--tt-primary)' : 'var(--tt-text)',
                                backgroundColor: subActive ? 'rgba(3,169,244,0.12)' : 'transparent',
                                textDecoration: 'none',
                                fontWeight: subActive ? '600' : '400',
                                transition: 'all 0.3s ease',
                                borderLeft: subActive ? '3px solid var(--tt-primary)' : '3px solid transparent'
                              }}
                            >
                              <sub.icon size={16} />
                              <span>{sub.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                    <Divider my="sm" />
                  </div>
                );
              })()}

              {/* The rest items */}
              {navigation.filter(n => n.href !== '/connect' && n.href !== '/settings' && n.href !== '/downloads').map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link 
                    key={item.href}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      margin: '4px 0',
                      borderRadius: '8px',
                      color: isActive ? 'var(--tt-primary)' : 'var(--tt-dark)',
                      backgroundColor: isActive ? 'rgba(3,169,244,0.10)' : 'transparent',
                      textDecoration: 'none',
                      fontWeight: isActive ? '600' : '500',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <item.icon size={20} />
                    <span>{item.name}</span>
                  </Link>
                );
              })}

              {/* Settings and Downloads as their own links with divider after */}
              {(() => {
                const settingsItem = navigation.find(n => n.href === '/settings');
                const downloadsItem = navigation.find(n => n.href === '/downloads');
                
                return (
                  <>
                    {settingsItem && (() => {
                      const isActive = location.pathname === settingsItem.href;
                      return (
                        <Link
                          key={settingsItem.href}
                          to={settingsItem.href}
                          onClick={() => setSidebarOpen(false)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px 16px',
                            margin: '4px 0',
                            borderRadius: '8px',
                            color: isActive ? 'var(--tt-primary)' : 'var(--tt-dark)',
                            backgroundColor: isActive ? 'rgba(3,169,244,0.10)' : 'transparent',
                            textDecoration: 'none',
                            fontWeight: isActive ? '600' : '500',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <settingsItem.icon size={20} />
                          <span>{settingsItem.name}</span>
                        </Link>
                      );
                    })()}
                    
                    {downloadsItem && (() => {
                      const isActive = location.pathname === downloadsItem.href;
                      return (
                        <Link
                          key={downloadsItem.href}
                          to={downloadsItem.href}
                          onClick={() => setSidebarOpen(false)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px 16px',
                            margin: '4px 0',
                            borderRadius: '8px',
                            color: isActive ? 'var(--tt-primary)' : 'var(--tt-dark)',
                            backgroundColor: isActive ? 'rgba(3,169,244,0.10)' : 'transparent',
                            textDecoration: 'none',
                            fontWeight: isActive ? '600' : '500',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <downloadsItem.icon size={20} />
                          <span>{downloadsItem.name}</span>
                        </Link>
                      );
                    })()}
                    
                    <Divider my="sm" />
                  </>
                );
              })()}

              {/* Admin parent moved below settings - only show for admins */}
              {isAdmin && (() => {
                const item = { name: t('navigation.admin') || 'Admin', href: '/admin/assets', icon: Shield };
                const isActive = adminGroupActive;
                return (
                  <div key="admin">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Link
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 16px',
                          margin: '4px 0',
                          borderRadius: '8px',
                          color: isActive ? 'var(--tt-primary)' : 'var(--tt-dark)',
                          backgroundColor: isActive ? 'rgba(3,169,244,0.10)' : 'transparent',
                          textDecoration: 'none',
                          fontWeight: isActive ? '600' : '500',
                          transition: 'all 0.3s ease',
                          borderLeft: isActive ? '3px solid var(--tt-primary)' : '3px solid transparent',
                          flex: 1
                        }}
                      >
                        <item.icon size={20} />
                        <span>{item.name}</span>
                      </Link>
                      <button
                        aria-label="toggle-admin"
                        onClick={() => setAdminOpen(v => !v)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: '8px',
                          cursor: 'pointer',
                          color: 'var(--tt-dark)'
                        }}
                      >
                        {adminOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      </button>
                    </div>
                    {/* Sub-links: Assets, Customers, Users */}
                    {adminOpen && (
                      <div style={{ marginLeft: '28px' }}>
                        {adminItems.map((sub) => {
                          const subActive = location.pathname === sub.href;
                          return (
                            <Link
                              key={sub.href}
                              to={sub.href}
                              onClick={() => setSidebarOpen(false)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '8px 12px',
                                margin: '2px 0',
                                borderRadius: '16px',
                                color: subActive ? 'var(--tt-primary)' : 'var(--tt-text)',
                                backgroundColor: subActive ? 'rgba(3,169,244,0.12)' : 'transparent',
                                textDecoration: 'none',
                                fontWeight: subActive ? '600' : '400',
                                transition: 'all 0.3s ease',
                                borderLeft: subActive ? '3px solid var(--tt-primary)' : '3px solid transparent'
                              }}
                            >
                              <sub.icon size={16} />
                              <span>{sub.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </nav>
          </div>
        )}
      </aside>

      {/* Main content (no margin shift) */}
      <main style={{
        flex: 1,
        marginLeft: '0px',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden'
      }}>
        {/* Top bar */}
        <header style={{
          background: 'var(--tt-white)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          // Keep header below dialogs/modals
          zIndex: 100
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button
              variant="subtle"
              size="sm"
              onClick={toggleSidebar}
              style={{ color: 'var(--tt-primary)' }}
            >
              <Menu size={20} />
            </Button>
            
            {/* Logo in top bar when sidebar is closed */}
            {!sidebarOpen && (
              <Link to="/summary" style={{ textDecoration: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Crop logo to icon only by hiding the text area on the right */}
                  <div style={{ width: '28px', height: '28px', overflow: 'hidden', display: 'inline-block' }}>
                    <img 
                      src={`${process.env.PUBLIC_URL || ''}/logo-dark-sm.png`} 
                      alt="TimberTrack Logo"
                      className="tt-invert-on-dark"
                      style={{ height: '28px', width: 'auto', display: 'block', objectFit: 'cover', objectPosition: 'left center' }}
                      onError={(e) => {
                        const base = process.env.PUBLIC_URL || '';
                        (e.currentTarget as HTMLImageElement).src = `${base}/logo192.png`;
                      }}
                    />
                  </div>
                  {/* Remove adjacent title to keep only the icon */}
                </div>
              </Link>
            )}
            
            {!isMobile && (
              <h1 style={{
                color: 'var(--tt-dark)',
                fontSize: '1.25rem',
                fontWeight: 500,
                margin: 0,
                marginLeft: !sidebarOpen ? '12px' : '0px'
              }}>
                {currentTitle}
              </h1>
            )}
          </div>
          {/* Center section: banner centered in header on wide screens */}
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            {isWide && (
              <img
                src={`${process.env.PUBLIC_URL || ''}/image.png`}
                alt="TimberTrack banner"
                className="tt-invert-on-dark"
                style={{
                  maxHeight: '40px',
                  width: 'auto',
                  display: 'block',
                  borderRadius: '6px'
                }}
                onLoad={() => setHeaderBannerLoaded(true)}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {!isMobile && (
              <Button 
                variant="filled" 
                size="sm"
                style={{ backgroundColor: 'var(--tt-primary)' }}
                onClick={() => navigate('/connect')}
              >
                {t('navigation.connect')}
              </Button>
            )}
            <MantineMenu position="bottom-end" shadow="md" width={280}>
              <MantineMenu.Target>
                <ActionIcon variant="subtle" aria-label="user-menu">
                  <Avatar size="sm" radius="xl" color="blue">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </Avatar>
                </ActionIcon>
              </MantineMenu.Target>
              <MantineMenu.Dropdown>
                <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                  <Text size="sm" fw={600}>{user?.name || 'User'}</Text>
                  <Text size="xs" c="dimmed">{user?.email || ''}</Text>
                  {isAdmin && (
                    <Text size="xs" c="blue" mt={4}>
                      <Shield size={12} style={{ display: 'inline', marginRight: '4px' }} />
                      Admin
                    </Text>
                  )}
                </div>
                <Divider my="xs" />
                <MantineMenu.Label>{t('common.language') || 'Language'}</MantineMenu.Label>
                <div style={{ padding: '4px 8px' }}>
                  <LanguageSwitcher />
                </div>
                <Divider my="xs" />
                <MantineMenu.Label>{t('common.theme') || 'Theme'}</MantineMenu.Label>
                <div style={{ padding: '4px 8px' }}>
                  <ThemeSwitcher />
                </div>
                <Divider my="xs" />
                {isMobile && (
                  <MantineMenu.Item onClick={() => navigate('/connect')} leftSection={<Home size={16} />}> 
                    {t('navigation.connect')}
                  </MantineMenu.Item>
                )}
                <MantineMenu.Item leftSection={<User size={16} />} onClick={() => navigate('/settings')}>
                  {t('navigation.settings') || 'Settings'}
                </MantineMenu.Item>
                <MantineMenu.Item leftSection={<HelpCircle size={16} />} onClick={() => navigate('/help')}>
                  {t('common.help') || 'Hilfe'}
                </MantineMenu.Item>
                <Divider my="xs" />
                <MantineMenu.Item 
                  leftSection={<LogOut size={16} />} 
                  onClick={logout}
                  color="red"
                >
                  {t('common.logout') || 'Logout'}
                </MantineMenu.Item>
              </MantineMenu.Dropdown>
            </MantineMenu>
          </div>
        </header>

        {/* Page content */}
        <div style={{
          flex: 1,
          padding: '24px',
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          minWidth: 0,
          overflowX: 'hidden'
        }}>
          {children}
        </div>

        {/* Footer */}
        <footer style={{
          background: 'var(--tt-white)',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          padding: '16px 24px',
          textAlign: 'center',
          color: 'var(--tt-text)',
          fontSize: '14px'
        }}>
          <div>
            <Link to="/privacy" style={{ color: 'var(--tt-primary)', textDecoration: 'none' }}>{t('footer.privacy')}</Link>
            <span style={{ margin: '0 8px' }}>•</span>
            <Link to="/imprint" style={{ color: 'var(--tt-primary)', textDecoration: 'none' }}>{t('footer.imprint')}</Link>
            <span style={{ margin: '0 8px' }}>•</span>
            <Link to="/terms" style={{ color: 'var(--tt-primary)', textDecoration: 'none' }}>{t('footer.terms')}</Link>
          </div>
          <div style={{ marginTop: '8px' }}>
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </div>
        </footer>
      </main>

      {/* Overlay for sidebar (all viewports) */}
      {sidebarOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            display: 'block'
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export { AppLayout };
export default AppLayout;
